// ThirdPartyShippingAccount.jsx
import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import ThirdPartyForm from './ThirdPartyForm';
import { createClientAccounts, getAllClientAccounts, getShippingCouriers, updateClientAccount } from '../../../services/shipping';
import toast from 'react-hot-toast';

const ThirdPartyShippingAccount = ({
  open,
  onClose,

  // ✅ generic setter for whichever dropdown is active (client or supplier)
  setAccounts,

  instanceUniqueId,

  // ✅ pass only ONE of these at a time (other must be null)
  customerId = null,
  supplierId = null,

  contextLabel = "",
  contextType = "customer",
  contextName = "",
  onCreated,
  initialData
}) => {
  const [formData, setFormData] = useState({
    courier: '',
    accountName: '',
    accountNumber: '',
    postalCode: '',
  });
  const [courierList, setCourierList] = useState([]);

  const isEditing = Boolean(initialData?.id);
  const isFormInvalid = Object.values(formData).some(v => !v);

  const handleSelect = (e, name) => {
    setFormData((prev) => ({ ...prev, [name]: e?.target?.value }));
  };

  const handleInputChange = (event, name) => {
    setFormData((prev) => ({ ...prev, [name]: event.target.value }));
  };

  useEffect(() => {
    if (!open) return;

    let active = true;
    getShippingCouriers()
      .then(({ couriers = [] } = {}) => {
        if (!active) return;
        const historicalCourier = String(initialData?.courier || '').trim();
        const includesHistorical = couriers.some(({ id }) => String(id) === historicalCourier);
        setCourierList(includesHistorical || !historicalCourier
          ? couriers
          : [...couriers, { id: historicalCourier, name: historicalCourier }]);
      })
      .catch(() => {
        const historicalCourier = String(initialData?.courier || '').trim();
        if (active) setCourierList(historicalCourier ? [{ id: historicalCourier, name: historicalCourier }] : []);
      });

    if (initialData) {
      setFormData({
        courier: initialData.courier || '',
        accountName: initialData.accountName || '',
        accountNumber: initialData.accountNumber || '',
        postalCode: initialData.postalZipCode || '',
      });
      return () => { active = false; };
    }

    setFormData({ courier: '', accountName: '', accountNumber: '', postalCode: '' });
    return () => { active = false; };
  }, [open, initialData]);

  const onSave = async () => {
    try {
      // ✅ enforce "one ID only"
      const activeCustomerId = customerId ? customerId : null;
      const activeSupplierId = supplierId ? supplierId : null;

      const payload = {
        courier: formData.courier,
        accountName: formData.accountName,
        accountNumber: formData.accountNumber,
        postalZipCode: formData.postalCode,
        instanceUniqueId,

        ...(activeCustomerId ? { customerId: activeCustomerId } : {}),
        ...(activeSupplierId ? { supplierId: activeSupplierId } : {}),
      };

      const response = isEditing
        ? await updateClientAccount(initialData.id, payload)
        : await createClientAccounts(payload);

      // ✅ refresh ONLY the correct pool
      if (setAccounts) {
        let accounts = await getAllClientAccounts({
          instanceUniqueId,
          ...(activeCustomerId ? { customerId: activeCustomerId } : {}),
          ...(activeSupplierId ? { supplierId: activeSupplierId } : {}),
        });

        accounts =
          accounts?.data?.map((account) => ({
            id: account.id,
            name: account.accountName,
            courier: account.courier || '',
          })) || [];

        setAccounts(accounts);
      }

      onCreated?.(response?.data);
      onClose(false);
      toast.success(isEditing ? "Third party account updated" : "Third party account created");
    } catch (error) {
      console.warn("Error on save", error);
      toast.error("Error on saving third party account");
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} fullWidth maxWidth="md">
      <DialogTitle className="create-order-dialog-title">
        {`Add Third Party Shipping Account for ${contextLabel}${contextName ? ` (${contextName})` : ""}`}
      </DialogTitle>
      <DialogContent>
        <ThirdPartyForm
          formData={formData}
          handleSelect={handleSelect}
          handleInputChange={handleInputChange}
          courierList={courierList}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} color="secondary">Cancel</Button>
        <Button variant="contained" onClick={onSave} disabled={isFormInvalid}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ThirdPartyShippingAccount;
